import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  InputAdornment,
  Box,
  Typography,
  Checkbox,
  Chip,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@mui/material";
import {
  Search,
  Close,
  ExpandMore,
  CheckCircle,
  LibraryAddCheck,
} from "@mui/icons-material";
import plantilla from "../data/plantillaConceptos.json";

// Normaliza para comparar/buscar: minúsculas, sin acentos, sin espacios extra.
const norm = (s) =>
  (s || "")
    .toString()
    .trim()
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");

// Agrupa la plantilla por agrupación una sola vez (orden de aparición).
const GRUPOS = (() => {
  const map = new Map();
  for (const item of plantilla) {
    if (!map.has(item.agrupacion)) map.set(item.agrupacion, []);
    map.get(item.agrupacion).push(item);
  }
  return Array.from(map.entries()).map(([agrupacion, items]) => ({
    agrupacion,
    items,
  }));
})();

function PlantillaConceptosDialog({ open, onClose, existingRecords, onAdd }) {
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(() => new Set());
  const [expanded, setExpanded] = useState(() => new Set());

  // Conceptos ya presentes en la tabla (para marcarlos como "Ya agregado").
  const yaAgregados = useMemo(() => {
    const s = new Set();
    for (const r of existingRecords || []) {
      if (r?.conceptoCatalogo?.trim()) s.add(norm(r.conceptoCatalogo));
    }
    return s;
  }, [existingRecords]);

  // Reinicia el estado cada vez que se abre el diálogo.
  useEffect(() => {
    if (open) {
      setQuery("");
      setSelected(new Set());
      setExpanded(new Set());
    }
  }, [open]);

  const q = norm(query);

  // Filtra grupos/items por el buscador (por concepto o por nombre de agrupación).
  const gruposFiltrados = useMemo(() => {
    if (!q) return GRUPOS;
    return GRUPOS.map((g) => {
      if (norm(g.agrupacion).includes(q)) return g;
      const items = g.items.filter((it) => norm(it.conceptoCatalogo).includes(q));
      return { ...g, items };
    }).filter((g) => g.items.length > 0);
  }, [q]);

  const isYaAgregado = (it) => yaAgregados.has(norm(it.conceptoCatalogo));

  const toggleItem = (codigo) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(codigo)) next.delete(codigo);
      else next.add(codigo);
      return next;
    });
  };

  const handleAccordionChange = (agrupacion) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(agrupacion)) next.delete(agrupacion);
      else next.add(agrupacion);
      return next;
    });
  };

  const handleConfirm = () => {
    const elegidos = plantilla
      .filter((it) => selected.has(it.codigo))
      .map(({ conceptoCatalogo, agrupacion, estadoFinanciero }) => ({
        conceptoCatalogo,
        agrupacion,
        estadoFinanciero,
      }));
    onAdd(elegidos);
  };

  const totalSeleccionados = selected.size;
  // Cuando hay búsqueda, abre los grupos automáticamente para no obligar a expandir.
  const grupoAbierto = (agrupacion) => (q ? true : expanded.has(agrupacion));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth
      PaperProps={{ sx: { height: "85vh" } }}>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <LibraryAddCheck color="primary" />
            <Typography variant="h6" component="span" sx={{ fontWeight: "bold" }}>
              Agregar desde plantilla
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" aria-label="Cerrar">
            <Close />
          </IconButton>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Elige los conceptos de tu negocio. Puedes buscar o seleccionar grupos completos.
        </Typography>
      </DialogTitle>

      <Box sx={{ px: 3, pb: 1 }}>
        <TextField
          fullWidth
          size="small"
          autoFocus
          placeholder="Buscar concepto o grupo…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
      </Box>
      <Divider />

      <DialogContent sx={{ p: 0, bgcolor: "grey.50" }}>
        {gruposFiltrados.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center", color: "text.secondary" }}>
            <Typography>No se encontraron conceptos para “{query}”.</Typography>
          </Box>
        ) : (
          gruposFiltrados.map((g) => {
            const seleccionados = g.items.filter((it) => selected.has(it.codigo)).length;
            const abierto = grupoAbierto(g.agrupacion);

            return (
              <Accordion
                key={g.agrupacion}
                expanded={abierto}
                onChange={() => handleAccordionChange(g.agrupacion)}
                disableGutters
                elevation={0}
                sx={{ "&:before": { display: "none" }, borderBottom: 1, borderColor: "divider" }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore sx={{ color: "primary.main" }} />}
                  sx={{
                    bgcolor: abierto ? "primary.50" : "background.paper",
                    transition: "background-color .15s",
                    "&:hover": { bgcolor: "action.hover" },
                    "& .MuiAccordionSummary-content": { alignItems: "center", gap: 1 },
                  }}
                >
                  <Typography sx={{ fontWeight: 600, flexGrow: 1 }}>
                    {g.agrupacion}
                  </Typography>
                  {seleccionados > 0 && (
                    <Chip
                      size="small"
                      color="primary"
                      label={`${seleccionados} sel.`}
                      sx={{ height: 20 }}
                    />
                  )}
                  <Chip
                    size="small"
                    variant="outlined"
                    label={g.items.length}
                    sx={{ height: 20 }}
                  />
                </AccordionSummary>
                <AccordionDetails sx={{ p: 0, bgcolor: "background.paper" }}>
                  <List dense disablePadding>
                    {g.items.map((it) => {
                      const ya = isYaAgregado(it);
                      const checked = selected.has(it.codigo);
                      return (
                        <ListItem key={it.codigo} disablePadding>
                          <ListItemButton
                            disabled={ya}
                            onClick={() => toggleItem(it.codigo)}
                            sx={{ pl: 4 }}
                          >
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {ya ? (
                                <CheckCircle color="success" fontSize="small" />
                              ) : (
                                <Checkbox
                                  edge="start"
                                  size="small"
                                  checked={checked}
                                  tabIndex={-1}
                                  disableRipple
                                />
                              )}
                            </ListItemIcon>
                            <ListItemText
                              primary={it.conceptoCatalogo}
                              secondary={ya ? "Ya agregado" : null}
                              primaryTypographyProps={{ variant: "body2" }}
                              secondaryTypographyProps={{ variant: "caption", color: "success.main" }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </DialogContent>

      <Divider />
      <DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
        <Typography variant="body2" color="text.secondary">
          {totalSeleccionados} seleccionado{totalSeleccionados === 1 ? "" : "s"}
        </Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} color="inherit">
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirm}
            disabled={totalSeleccionados === 0}
            startIcon={<LibraryAddCheck />}
          >
            Agregar {totalSeleccionados > 0 ? totalSeleccionados : ""}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

export default PlantillaConceptosDialog;
